import { useEffect, useState } from 'react';
import { useResource } from '../hooks/useResource';
import { api } from '../lib/api';

interface CustomAttrDef {
  id: string;
  scope: 'contact' | 'conversation';
  key: string;
  label: string;
  type: 'text' | 'number' | 'link' | 'date' | 'list' | 'checkbox';
  options?: string[];
  required: boolean;
}

interface Props {
  tenantCode: string;
  agentUid: string;
  chatId: string;
  values: Record<string, unknown>;
}

export function CustomAttrPanel({ tenantCode, agentUid, chatId, values }: Props) {
  const { items: defs } = useResource<CustomAttrDef>('custom_attributes');
  const conversationDefs = defs.filter((d) => d.scope === 'conversation');
  const [draft, setDraft] = useState<Record<string, unknown>>(values);
  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => setDraft(values), [JSON.stringify(values)]);

  async function commit(key: string, value: unknown) {
    if (JSON.stringify(value) === JSON.stringify(values[key])) return;
    setSaving(key);
    try {
      await api.setCustomAttr(tenantCode, agentUid, chatId, key, value);
    } finally {
      setSaving(null);
    }
  }

  if (conversationDefs.length === 0) return null;

  return (
    <div className="flex-shrink-0" style={{ padding: 12, borderTop: '1px solid #e5e7eb', background: 'white' }}>
      <div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', marginBottom: 6 }}>
        Conversation attributes
      </div>
      {conversationDefs.map((def) => {
        const v = draft[def.key];
        const onCommit = (val: unknown) => commit(def.key, val);
        return (
          <div key={def.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <label
              style={{
                width: 140,
                fontSize: 12,
                color: '#374151',
                fontWeight: def.required ? 600 : 400,
              }}
            >
              {def.label}
              {def.required && <span style={{ color: '#dc2626' }}> *</span>}
            </label>
            <div style={{ flex: 1 }}>{renderInput(def, v, (val) => setDraft({ ...draft, [def.key]: val }), onCommit)}</div>
            {saving === def.key && <span style={{ fontSize: 11, color: '#6b7280' }}>saving…</span>}
          </div>
        );
      })}
    </div>
  );
}

function renderInput(
  def: CustomAttrDef,
  value: unknown,
  onChange: (v: unknown) => void,
  onCommit: (v: unknown) => void
) {
  const common = {
    style: {
      width: '100%',
      border: '1px solid #d1d5db',
      borderRadius: 4,
      padding: '4px 8px',
      fontSize: 13,
    } as React.CSSProperties,
    onBlur: () => onCommit(value),
  };
  switch (def.type) {
    case 'number':
      return (
        <input
          type="number"
          value={(value as number | undefined) ?? ''}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
          {...common}
        />
      );
    case 'link':
      return (
        <input
          type="url"
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          {...common}
        />
      );
    case 'date':
      return (
        <input
          type="date"
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          {...common}
        />
      );
    case 'checkbox':
      return (
        <input
          type="checkbox"
          checked={!!value}
          onChange={(e) => {
            onChange(e.target.checked);
            onCommit(e.target.checked);
          }}
        />
      );
    case 'list':
      return (
        <select
          value={(value as string) ?? ''}
          onChange={(e) => {
            onChange(e.target.value);
            onCommit(e.target.value);
          }}
          {...common}
        >
          <option value="">—</option>
          {(def.options ?? []).map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      );
    case 'text':
    default:
      return (
        <input
          type="text"
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          {...common}
        />
      );
  }
}
